import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { confirm, haptic } from '../lib/telegram';
import AmountInput from '../components/AmountInput';
import { parseAmountInput, formatAmount } from '../lib/format';
import type { Preset, Currency } from '../lib/types';

interface Props { onBack: () => void; }

export default function Presets({ onBack }: Props) {
  const [items, setItems] = useState<Preset[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  const [label, setLabel] = useState('');
  const [emoji, setEmoji] = useState('');
  const [currency, setCurrency] = useState<Currency>('SYP');
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState<'expense' | 'income'>('expense');
  const [category, setCategory] = useState<string>('');

  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setErr(null);
    try {
      const [p, c] = await Promise.all([api.listPresets(), api.listCategories()]);
      setItems(p.presets);
      setCategories(c.categories);
      if (!category) setCategory(c.categories[0] || '');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const amt = parseAmountInput(amount);
    if (!label.trim()) { setErr('Label required'); return; }
    if (amt === null) { setErr('Amount must be a positive number'); return; }
    setErr(null); setBusy(true);
    try {
      await api.addPreset({
        label: label.trim(),
        emoji: emoji.trim() || undefined,
        currency, amount: amt, kind,
        category: kind === 'expense' ? category : null,
      });
      setLabel(''); setEmoji(''); setAmount('');
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed');
    } finally { setBusy(false); }
  }

  async function remove(p: Preset) {
    if (!(await confirm(`Remove preset "${p.label}"?`))) return;
    setBusy(true);
    try {
      await api.removePreset(p.id);
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed');
    } finally { setBusy(false); }
  }

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>Quick presets</h1>
      </div>
      {err && <div className="error">{err}</div>}

      <form className="card" onSubmit={add}>
        <label className="label" style={{ marginTop: 0 }}>New preset</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="input" style={{ flex: '0 0 70px' }} value={emoji} onChange={(e) => setEmoji(e.target.value)} placeholder="☕" maxLength={4} />
          <input className="input" style={{ flex: 1 }} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Coffee" maxLength={40} />
        </div>

        <label className="label">Currency</label>
        <div className="segmented">
          <button type="button" className={currency === 'SYP' ? 'active' : ''} onClick={() => setCurrency('SYP')}>SYP</button>
          <button type="button" className={currency === 'USD' ? 'active' : ''} onClick={() => setCurrency('USD')}>USD</button>
        </div>

        <label className="label">Amount</label>
        <AmountInput value={amount} onChange={setAmount} currency={currency} />

        <label className="label">Kind</label>
        <div className="segmented">
          <button type="button" className={kind === 'expense' ? 'active' : ''} onClick={() => setKind('expense')}>Expense</button>
          <button type="button" className={kind === 'income' ? 'active' : ''} onClick={() => setKind('income')}>Income</button>
        </div>

        {kind === 'expense' && (
          <>
            <label className="label">Category</label>
            <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </>
        )}

        <button type="submit" className="btn" disabled={busy} style={{ marginTop: 12 }}>
          {busy ? 'Saving…' : 'Add preset'}
        </button>
      </form>

      <div className="card">
        <div className="muted small" style={{ marginBottom: 8 }}>Your presets</div>
        {loading ? <div className="muted">Loading…</div> : items.length === 0 ? (
          <div className="empty">No presets yet. Add one above.</div>
        ) : items.map((p) => (
          <div key={p.id} className="row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div>{p.emoji ? `${p.emoji} ` : ''}{p.label}</div>
              <div className="muted small">
                {p.kind === 'income' ? '+ ' : '− '}{formatAmount(Math.abs(Number(p.amount)), p.currency)}
                {p.category ? ` · ${p.category}` : ''}
              </div>
            </div>
            <button className="btn-danger" style={{ background: 'transparent', border: 'none', padding: '4px 8px', cursor: 'pointer' }} onClick={() => remove(p)}>Remove</button>
          </div>
        ))}
      </div>
    </>
  );
}
